import React, { FunctionComponent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Flex, Stack } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { useAppUpdatesContext } from '@app/app';
import SentryLogger from '@app/sentry';
import { BundledChangesFilters } from '@app/app-updates-table-filters';
import { BundledChangesTable } from '@app/app-updates-table';
import { getProductType, getProductConfig } from '@app/app-updates-product-app';
import type { UiProductType, ProductKey } from '@app/app-updates-product-app';
import { BundleChangesResponseSchema } from './schema';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import UFOSegment from '@atlaskit/react-ufo/segment';

interface BundleFilterParams {
  search?: string;
  products?: string;
  inputDate?: string;
}

type BundleQueryParams =
  | { cursor: string }
  | (BundleFilterParams & { limit: number });

export const BundledChangesContent: FunctionComponent = () => {
  const { api, dispatch, state } = useAppUpdatesContext();
  const { committedFilters, pagination } = state.bundles;
  const {
    selectedProducts: committedProducts,
    selectedBundleDate: committedBundleDate,
    searchQuery: committedSearchQuery,
    committedFiltersVersion,
  } = committedFilters;

  const selectedProducts =
    committedProducts
      ?.map(({ id }) => getProductConfig(id as UiProductType)?.productKey)
      .filter((key): key is ProductKey => key !== undefined)
      .join(',') || undefined;

  // Prefetch available bundle dates for the currently selected app(s) so the
  // most recent date can be auto-applied before we render the table.
  const datesQuery = useQuery({
    queryKey: [
      '/gateway/api/public/app-updates/v1/bundle-dates',
      selectedProducts ?? null,
    ],
    queryFn: async () => {
      const response = await api.get<string[]>(
        '/gateway/api/public/app-updates/v1/bundle-dates',
        { params: { products: selectedProducts } },
      );
      return response.data ?? [];
    },
  });

  const hasInitialisedDefaultDateRef = React.useRef(false);
  const hasUserSelectedDate = committedBundleDate !== null;
  const isReadyToRender =
    hasInitialisedDefaultDateRef.current || hasUserSelectedDate;

  React.useEffect(() => {
    if (hasInitialisedDefaultDateRef.current || hasUserSelectedDate) {
      return;
    }
    if (datesQuery.isSuccess) {
      const mostRecentDate = datesQuery.data[0] ?? null;
      hasInitialisedDefaultDateRef.current = true;
      if (mostRecentDate) {
        dispatch({
          type: 'BUNDLE_FILTER_DATE_SELECT',
          payload: { selectedBundleDate: mostRecentDate },
        });
        dispatch({ type: 'BUNDLES_FILTERS_APPLY' });
      }
    }
  }, [datesQuery.isSuccess, datesQuery.data, hasUserSelectedDate, dispatch]);
  const searchQuery = committedSearchQuery || undefined;
  const inputDate = committedBundleDate || undefined;

  const bundlesQuery = useQuery({
    enabled: isReadyToRender,
    queryKey: [
      '/gateway/api/public/app-updates/v1/bundle-changes',
      pagination.cursor ?? null,
      pagination.limit,
      searchQuery ?? null,
      selectedProducts ?? null,
      inputDate ?? null,
      committedFiltersVersion,
    ],
    placeholderData: (previousData) => previousData,
    queryFn: async () => {
      try {
        const params: BundleQueryParams = pagination.cursor
          ? { cursor: pagination.cursor }
          : {
              limit: pagination.limit,
              search: searchQuery,
              products: selectedProducts,
              inputDate,
            };

        const response = await api.get(
          '/gateway/api/public/app-updates/v1/bundle-changes',
          { params },
        );

        const parsedResponse = BundleChangesResponseSchema.parse(response.data);
        return {
          bundles: [
            ...(parsedResponse?.data.map((bundle) => ({
              ...bundle,
              productTypes:
                bundle.products
                  ?.map((product) => getProductType(product))
                  .filter(
                    (product: UiProductType | null): product is UiProductType =>
                      product != null,
                  ) || [],
            })) || []),
          ],
          links: parsedResponse?.links,
        };
      } catch (error) {
        const isParseError =
          error instanceof Error && error.name === 'ZodError';
        SentryLogger.error(error, {
          extra: {
            action: isParseError
              ? 'parse-bundle-changes-response'
              : 'fetch-bundle-changes',
          },
        });
        throw error;
      }
    },
  });

  React.useEffect(() => {
    dispatch({
      type: 'BUNDLES_LOADING_SET',
      payload: { isLoading: bundlesQuery.isFetching },
    });
  }, [dispatch, bundlesQuery.isFetching]);

  React.useEffect(() => {
    dispatch({
      type: 'BUNDLES_ERROR_SET',
      payload: { error: bundlesQuery.error?.message || null },
    });
  }, [dispatch, bundlesQuery.error]);

  React.useEffect(() => {
    dispatch({
      type: 'BUNDLES_DATA_LINKS_SET',
      payload: {
        data: bundlesQuery.data?.bundles || [],
        links: bundlesQuery.data?.links
          ? bundlesQuery.data.links
          : { self: '', next: null, prev: null },
      },
    });
  }, [dispatch, bundlesQuery.data]);

  React.useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.scrollTo !== 'function'
    ) {
      return;
    }
    window.scrollTo({ top: 0, left: 0 });
  }, [pagination.cursor]);

  return (
    <UFOSegment name="bundled-changes">
      <UFOLoadHold
        name="bundles-data"
        hold={!isReadyToRender || bundlesQuery.isFetching}
      />
      {isReadyToRender ? (
        <Stack space="space.400">
          <BundledChangesFilters />
          <BundledChangesTable />
        </Stack>
      ) : (
        <Flex justifyContent="center" alignItems="center">
          <Box padding="space.400">
            <Spinner size="large" />
          </Box>
        </Flex>
      )}
    </UFOSegment>
  );
};
